import React from 'react';
import { DetailsHeader } from './DetailsHeader';
import { SharedNotesTimeline } from './SharedNotesTimeline';
import { ValueLabel } from '@shared/components/ValueLabel';
import { RefreshCw, User, Info, CreditCard } from 'lucide-react';
import { useRequestDetail } from '../../../shared/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';

export const TransferDetails = ({ data }: { data: any }) => {
  const { t } = useTranslation();
  const { remoteUtilityId } = getLoginDataFromStorage();
  const { data: requestDetail, isLoading } = useRequestDetail({
    remote_utility_id: remoteUtilityId,
    id: data?.id,
  });

  const details = requestDetail?.result || data;

  if (isLoading) return <div className="p-6 text-center text-slate-500">{t("Loading...")}</div>;

  return (
    <div className="flex flex-col h-full bg-slate-50/50 pb-6">
      <DetailsHeader 
        icon={<RefreshCw size={18} />}
        iconBgColor="bg-blue-100"
        iconTextColor="text-blue-600"
        title={details?.utilitySupportRequest?.name || t("Transfer Request")}
        status={details?.statusDisplay}
        id={details?.requestNo ?? t("N/A")}
        submittedDate={details?.createdDate ?? t("N/A")}
      />
      <div className="p-6 space-y-4">
        {/* TRANSFER INFO & TRANSFER TO */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><RefreshCw size={16}/> {t("Transfer Info")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Transfer Type")} value={details?.utilitySupportRequest?.supportRequestType || t("Ownership Transfer")} />
              <ValueLabel label={t("Effective Date")} value={details?.requestDate ? new Date(details.requestDate).toLocaleDateString() : t("N/A")} />
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><User size={16}/> {t("Transfer To")}</h4>
            <div className="space-y-4">
              <ValueLabel label={t("Transfer To")} value={details?.additionalData?.transferTo || t("N/A")}/>
              <ValueLabel label={t("Submitted")} value={details?.createdDate ? new Date(details.createdDate).toLocaleDateString() : t("N/A")} />
            </div>
          </div>
        </div>

        {/* NEW OWNER INFO */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><User size={16}/> {t("New Owner / Consumer Information")}</h4>
          <div className="grid grid-cols-2 gap-y-4 gap-x-4">
            <ValueLabel label={t("Name")} value={details?.additionalData?.newConsumerData?.firstName ? `${details.additionalData.newConsumerData.firstName} ${details.additionalData.newConsumerData.lastName || ''}` : t("N/A")} />
            <ValueLabel label={t("Email")} value={details?.additionalData?.newConsumerData?.email || t("N/A")} />
            <ValueLabel label={t("Phone")} value={details?.additionalData?.newConsumerData?.contactNumber || t("N/A")} />
            <ValueLabel label={t("Relationship")} value={details?.additionalData?.relationship || t("N/A")} />
          </div>
        </div>

        {/* FINANCIAL RESPONSIBILITY */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-4"><CreditCard size={16}/> {t("Financial Responsibility")}</h4>
          <ValueLabel label={t("Outstanding Balance Decision")} value={details?.additionalData?.transferOutstandingBalance === true ? t("Transfer outstanding balance to new account holder") : t("Clear Outstanding Balance")} />
        </div>

        {/* SPECIAL INSTRUCTIONS */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3"><Info size={16}/> {t("Special Instructions")}</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{details?.additionalData?.specialInstructions || t("No special instructions provided.")}</p>
        </div>

        {/* TABS */}
        <SharedNotesTimeline data={data} />
      </div>
    </div>
  );
};
